import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase/config';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import {
    Briefcase,
    Clock,
    CheckCircle,
    DollarSign,
    TrendingUp,
    Calendar,
    MapPin,
    Eye,
    AlertCircle,
    Award,    // ✅ Phase 2B: Added
    Target    // ✅ Phase 2B: Added
} from 'lucide-react';

export default function ContractorDashboard() {
    const { currentUser, userProfile } = useAuth();
    const [loading, setLoading] = useState(true);
    const [showMetricsDetail, setShowMetricsDetail] = useState(false);  // ✅ Phase 2B: Added
    const [stats, setStats] = useState({
        activeJobs: 0,
        completedJobs: 0,
        totalEarnings: 0,
        pendingEarnings: 0,
        currentPhase: 'trainee',
        hourlyRate: 18
    });
    const [myJobs, setMyJobs] = useState([]);

    // Real-time listener for contractor's assigned jobs
    useEffect(() => {
        if (!currentUser) return;

        console.log('🔍 Loading jobs for contractor:', currentUser.uid);

        const q = query(
            collection(db, 'workOrders'),
            where('contractorId', '==', currentUser.uid)
        );

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                console.log('✅ Jobs loaded:', snapshot.docs.length);
                const jobs = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                // Sort in JavaScript instead of Firestore
                jobs.sort((a, b) => {
                    const dateA = new Date(a.updatedAt || a.createdAt);
                    const dateB = new Date(b.updatedAt || b.createdAt);
                    return dateB - dateA; // Newest first
                });

                setMyJobs(jobs);
                calculateStats(jobs);
                setLoading(false);
            },
            (error) => {
                console.error('❌ Error loading jobs:', error);
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [currentUser]);

    const calculateStats = (jobs) => {
        const active = jobs.filter(j => 
            j.status === 'assigned' || j.status === 'in-progress'
        ).length;

        const completed = jobs.filter(j => j.status === 'completed').length;

        // Calculate earnings
        const totalEarnings = jobs
            .filter(j => j.status === 'completed' && j.contractorPaid)
            .reduce((sum, job) => sum + (job.contractorPayment || 0), 0);

        const pendingEarnings = jobs
            .filter(j => j.status === 'completed' && !j.contractorPaid)
            .reduce((sum, job) => sum + (job.contractorPayment || 0), 0);

        setStats({
            activeJobs: active,
            completedJobs: completed,
            totalEarnings,
            pendingEarnings,
            currentPhase: userProfile?.phase || 'trainee',
            hourlyRate: userProfile?.hourlyRate || 18
        });
    };

    const getStatusColor = (status) => {
        const colors = {
            'assigned': 'bg-blue-100 text-blue-800',
            'in-progress': 'bg-yellow-100 text-yellow-800',
            'completed': 'bg-green-100 text-green-800',
            'cancelled': 'bg-red-100 text-red-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const getPhaseInfo = (phase) => {
        const phases = {
            'shadow': { name: 'Shadow', rate: 0, color: 'gray' },
            'trainee': { name: 'Trainee', rate: 18, color: 'blue' },
            'junior': { name: 'Junior Tech', rate: 22, color: 'purple' },
            'standard': { name: 'Standard Tech', rate: 25, color: 'green' },
            'experienced': { name: 'Experienced Tech', rate: 27, color: 'yellow' }
        };
        return phases[phase] || phases['trainee'];
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading your dashboard...</p>
                </div>
            </div>
        );
    }

    const phaseInfo = getPhaseInfo(stats.currentPhase);

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">
                        Welcome back, {userProfile?.name || 'Contractor'}!
                    </h1>
                    <p className="text-gray-600 mt-2">
                        Here's your work overview for today
                    </p>
                </div>

                {/* ✅ Phase 2B: Enhanced Phase Badge with Metrics */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`mb-6 bg-${phaseInfo.color}-50 border-2 border-${phaseInfo.color}-200 rounded-xl p-6`}
                >
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                            <div className={`w-12 h-12 rounded-full bg-${phaseInfo.color}-600 flex items-center justify-center`}>
                                <TrendingUp className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">Current Level</p>
                                <p className={`text-xl font-bold text-${phaseInfo.color}-800`}>
                                    {phaseInfo.name}
                                </p>
                            </div>
                        </div>
                        <div className="text-right">
                            <p className="text-sm text-gray-600">Hourly Rate</p>
                            <p className={`text-2xl font-bold text-${phaseInfo.color}-800`}>
                                ${stats.hourlyRate}/hr
                            </p>
                        </div>
                    </div>

                    {/* ✅ Phase 2B: Eligibility Badge */}
                    {userProfile?.metrics?.eligibleForPromotion && (
                        <div className="mt-4 p-4 bg-green-100 border-2 border-green-300 rounded-lg">
                            <div className="flex items-center space-x-3">
                                <Award className="w-6 h-6 text-green-600" />
                                <div className="flex-1">
                                    <p className="font-bold text-green-900">
                                        Ready for Promotion!
                                    </p>
                                    <p className="text-sm text-green-700 mt-1">
                                        You've met the criteria for {userProfile.metrics.nextPhase}. 
                                        Admin will review your performance for promotion.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ✅ Phase 2B: Progress to Next Level */}
                    {userProfile?.metrics?.criteriaStatus && !userProfile?.metrics?.eligibleForPromotion && (
                        <div className="mt-4">
                            <div className="flex items-center justify-between mb-3">
                                <p className="text-sm font-medium text-gray-700">
                                    Progress to {userProfile.metrics.nextPhase}:
                                </p>
                                <button
                                    onClick={() => setShowMetricsDetail(!showMetricsDetail)}
                                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                                >
                                    {showMetricsDetail ? 'Hide Details' : 'Show Details'}
                                </button>
                            </div>

                            {showMetricsDetail && (
                                <div className="space-y-3">
                                    {Object.keys(userProfile.metrics.criteriaStatus).map((key) => {
                                        const status = userProfile.metrics.criteriaStatus[key];
                                        const percentage = (status.current / status.required) * 100;
                                        const isMet = status.met;

                                        return (
                                            <div key={key} className="bg-white rounded-lg p-3">
                                                <div className="flex items-center justify-between text-sm mb-2">
                                                    <span className="text-gray-700 capitalize font-medium">
                                                        {key.replace(/([A-Z])/g, ' $1').trim()}
                                                    </span>
                                                    <span className={`font-bold ${isMet ? 'text-green-600' : 'text-gray-900'}`}>
                                                        {isMet ? '✓' : ''} {status.current} / {status.required}
                                                    </span>
                                                </div>
                                                <div className="w-full bg-gray-200 rounded-full h-2.5">
                                                    <div
                                                        className={`h-2.5 rounded-full transition-all ${
                                                            isMet ? 'bg-green-500' : 'bg-blue-500'
                                                        }`}
                                                        style={{ width: `${Math.min(percentage, 100)}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </motion.div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {/* Active Jobs */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Active Jobs</p>
                                <p className="text-3xl font-bold text-gray-900 mt-2">
                                    {stats.activeJobs}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                                <Briefcase className="w-6 h-6 text-blue-600" />
                            </div>
                        </div>
                    </motion.div>

                    {/* Completed Jobs */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Completed</p>
                                <p className="text-3xl font-bold text-gray-900 mt-2">
                                    {stats.completedJobs}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                                <CheckCircle className="w-6 h-6 text-green-600" />
                            </div>
                        </div>
                    </motion.div>

                    {/* Total Earnings */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Total Earnings</p>
                                <p className="text-3xl font-bold text-gray-900 mt-2">
                                    ${stats.totalEarnings.toFixed(2)}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                                <DollarSign className="w-6 h-6 text-green-600" />
                            </div>
                        </div>
                    </motion.div>

                    {/* Pending Payment */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Pending Payment</p>
                                <p className="text-3xl font-bold text-gray-900 mt-2">
                                    ${stats.pendingEarnings.toFixed(2)}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                                <Clock className="w-6 h-6 text-yellow-600" />
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* ✅ Phase 2B: Performance Metrics */}
                {userProfile?.metrics && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        className="mb-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6"
                    >
                        <div className="flex items-center space-x-2 mb-4">
                            <Target className="w-5 h-5 text-blue-600" />
                            <h2 className="text-lg font-bold text-gray-900">Your Performance</h2>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {/* Completion Rate */}
                            <div className="text-center p-4 bg-gray-50 rounded-lg">
                                <p className="text-2xl font-bold text-green-600">
                                    {userProfile.metrics.completionRate || 0}%
                                </p>
                                <p className="text-xs text-gray-600 mt-1">Completion Rate</p>
                            </div>

                            {/* Average Rating */}
                            <div className="text-center p-4 bg-gray-50 rounded-lg">
                                <p className="text-2xl font-bold text-yellow-600">
                                    {userProfile.metrics.averageRating 
                                        ? userProfile.metrics.averageRating.toFixed(1) 
                                        : '0.0'}
                                </p>
                                <p className="text-xs text-gray-600 mt-1">Avg Rating</p>
                            </div>

                            {/* Supervised Jobs */}
                            <div className="text-center p-4 bg-gray-50 rounded-lg">
                                <p className="text-2xl font-bold text-blue-600">
                                    {userProfile.metrics.supervisedJobs || 0}
                                </p>
                                <p className="text-xs text-gray-600 mt-1">Supervised Jobs</p>
                            </div>

                            {/* Independent Jobs */}
                            <div className="text-center p-4 bg-gray-50 rounded-lg">
                                <p className="text-2xl font-bold text-purple-600">
                                    {userProfile.metrics.independentJobs || 0}
                                </p>
                                <p className="text-xs text-gray-600 mt-1">Independent Jobs</p>
                            </div>
                        </div>

                        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                            <p className="text-sm text-blue-800">
                                💡 Your metrics are automatically calculated based on your job performance. 
                                Complete more jobs and maintain high quality to advance to the next phase!
                            </p>
                        </div>
                    </motion.div>
                )}

                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <Link
                        to="/contractor/available-jobs"
                        className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold mb-2">View Available Jobs</h3>
                                <p className="text-blue-100 text-sm">
                                    Browse and express interest in new opportunities
                                </p>
                            </div>
                            <Briefcase className="w-12 h-12 opacity-80" />
                        </div>
                    </Link>

                    <Link
                        to="/contractor/my-jobs"
                        className="bg-gradient-to-r from-green-600 to-green-700 rounded-xl p-6 text-white hover:from-green-700 hover:to-green-800 transition-all shadow-lg hover:shadow-xl"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold mb-2">My Assigned Jobs</h3>
                                <p className="text-green-100 text-sm">
                                    Manage your current work assignments
                                </p>
                            </div>
                            <CheckCircle className="w-12 h-12 opacity-80" />
                        </div>
                    </Link>
                </div>

                {/* Recent Jobs */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className="bg-white rounded-xl shadow-sm border border-gray-200"
                >
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-xl font-bold text-gray-900">Recent Activity</h2>
                    </div>

                    {myJobs.length > 0 ? (
                        <div className="divide-y divide-gray-200">
                            {myJobs.slice(0, 5).map((job) => (
                                <Link
                                    key={job.id}
                                    to={`/contractor/jobs/${job.id}`}
                                    className="block p-6 hover:bg-gray-50 transition-colors"
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3 mb-2">
                                                <h3 className="font-semibold text-gray-900">
                                                    {job.serviceType}
                                                </h3>
                                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                                                    {job.status}
                                                </span>
                                            </div>
                                            
                                            <div className="flex items-center space-x-4 text-sm text-gray-600">
                                                <div className="flex items-center space-x-1">
                                                    <MapPin className="w-4 h-4" />
                                                    <span>{job.location}</span>
                                                </div>
                                                <div className="flex items-center space-x-1">
                                                    <Calendar className="w-4 h-4" />
                                                    <span>
                                                        {new Date(job.preferredDate).toLocaleDateString()}
                                                    </span>
                                                </div>
                                                {job.contractorPayment && (
                                                    <div className="flex items-center space-x-1">
                                                        <DollarSign className="w-4 h-4" />
                                                        <span>${job.contractorPayment}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="ml-4">
                                            <Eye className="w-5 h-5 text-gray-400" />
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    ) : (
                        <div className="p-12 text-center">
                            <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500">No jobs assigned yet</p>
                            <p className="text-sm text-gray-400 mt-1">
                                Check available jobs to get started
                            </p>
                        </div>
                    )}
                </motion.div>
            </div>
        </div>
    );
}
